/**
 * - Search Feature: arrow-key navigation, screen reader announcements.
 * - Cart Drawer: Focus trapping, return focus, screen reader announcements.
 */
(function () {
   'use strict';

   // ============================================================================
   // 1. GLOBAL ARIA-LIVE ANNOUNCER
   // ============================================================================
   var liveRegion = document.createElement('div');
   liveRegion.setAttribute('aria-live', 'polite');
   liveRegion.setAttribute('aria-atomic', 'true');
   liveRegion.style.cssText = [
      'position:absolute',
      'width:1px',
      'height:1px',
      'overflow:hidden',
      'clip:rect(0,0,0,0)',
      'white-space:nowrap',
      'border:0'
   ].join(';');

   function appendLiveRegion() {
      if (document.body && !document.body.contains(liveRegion)) {
         document.body.appendChild(liveRegion);
      }
   }

   function announce(message) {
      // Clear first so repeated identical messages still fire
      liveRegion.textContent = '';
      setTimeout(function () {
         liveRegion.textContent = message;
      }, 50);
   }

   // ============================================================================
   // 2. PREDICTIVE SEARCH BAR ADA FIX
   // ============================================================================
   function applySearchPatch() {
      if (
         !window.PXUTheme ||
         !window.PXUTheme.predictiveSearch ||
         window.PXUTheme.predictiveSearch._adaPatched
      ) return;

      window.PXUTheme.predictiveSearch._adaPatched = true;
      console.log('ADA Fixes: Predictive Search Patched!');

      // Per-input focus index map to support multiple search forms (e.g. mobile + desktop)
      var focusIndexMap = new WeakMap();

      function getIndex(input) {
         return focusIndexMap.has(input) ? focusIndexMap.get(input) : -1;
      }

      function setIndex(input, val) {
         focusIndexMap.set(input, val);
      }

      function resetIndex(input) {
         focusIndexMap.set(input, -1);
      }

      function setupSearchInputs() {
         var inputs = document.querySelectorAll('.search-form input[type="text"]');
         inputs.forEach(function (input, i) {
            if (input.hasAttribute('role')) return; // already set up

            input.id = input.id || ('search-input-' + i);
            input.setAttribute('role', 'combobox');
            input.setAttribute('aria-autocomplete', 'list');
            input.setAttribute('aria-expanded', 'false');
            input.setAttribute('aria-haspopup', 'listbox');

            var form = input.closest('form');
            var resultsWrapper = form && form.querySelector('.search__results-wrapper');
            if (resultsWrapper) {
               var listboxId = 'search-results-listbox-' + i;
               resultsWrapper.id = listboxId;
               resultsWrapper.setAttribute('role', 'listbox');
               input.setAttribute('aria-controls', listboxId);
            }
         });
      }
      setupSearchInputs();

      function updateVisualFocus(input, suggestions) {
         var idx = getIndex(input);
         suggestions.forEach(function (item) {
            item.setAttribute('aria-selected', 'false');
            item.style.outline = '';
            item.style.backgroundColor = '';
         });

         if (idx >= 0 && idx < suggestions.length) {
            var active = suggestions[idx];
            active.setAttribute('aria-selected', 'true');
            active.style.outline = '2px solid #000';
            active.style.backgroundColor = '#f5f5f5';
            input.setAttribute('aria-activedescendant', active.id);

            if (typeof active.scrollIntoViewIfNeeded === 'function') {
               active.scrollIntoViewIfNeeded(false);
            } else {
               active.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
         } else {
            input.removeAttribute('aria-activedescendant');
         }
      }

      document.addEventListener('keydown', function (e) {
         var input = e.target;
         if (!input || !input.matches('.search-form input[type="text"]')) return;

         var form = input.closest('form');
         if (!form) return;

         var resultsWrapper = form.querySelector('.search__results-wrapper');
         if (!resultsWrapper) return;

         var suggestions = Array.from(resultsWrapper.querySelectorAll('.search__results__item'));
         if (suggestions.length === 0) return;

         var idx = getIndex(input);

         if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIndex(input, idx >= suggestions.length - 1 ? 0 : idx + 1);
            updateVisualFocus(input, suggestions);
         } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIndex(input, idx <= 0 ? suggestions.length - 1 : idx - 1);
            updateVisualFocus(input, suggestions);
         } else if (e.key === 'Enter') {
            if (idx >= 0 && idx < suggestions.length) {
               e.preventDefault();
               var link = suggestions[idx].querySelector('a');
               if (link) window.location.href = link.href;
            }
         } else if (e.key === 'Escape') {
            resetIndex(input);
            window.PXUTheme.predictiveSearch.clearResults();
            input.removeAttribute('aria-activedescendant');
         }
      }, true);

      // Wrap displayResults
      var originalDisplay = window.PXUTheme.predictiveSearch.displayResults;
      window.PXUTheme.predictiveSearch.displayResults = function (results, $this) {
         originalDisplay.apply(this, arguments);

         var input = $this.find('input[type="text"]')[0];
         if (!input) return;

         resetIndex(input);
         input.setAttribute('aria-expanded', 'true');
         input.setAttribute('role', 'combobox');
         input.setAttribute('aria-autocomplete', 'list');
         input.setAttribute('aria-haspopup', 'listbox');

         var resultsWrapper = $this.find('.search__results-wrapper')[0];
         if (!resultsWrapper) return;

         var listboxId = (input.id || 'search-results') + '-listbox';
         resultsWrapper.id = listboxId;
         resultsWrapper.setAttribute('role', 'listbox');
         input.setAttribute('aria-controls', listboxId);

         var items = resultsWrapper.querySelectorAll('.search__results__item');
         items.forEach(function (item, i) {
            item.setAttribute('role', 'option');
            item.id = listboxId + '-option-' + i;
            item.setAttribute('aria-selected', 'false');
            var link = item.querySelector('a');
            if (link) link.setAttribute('tabindex', '-1');
         });

         announce(
            items.length > 0
               ? items.length + ' search suggestion' + (items.length === 1 ? '' : 's') + ' available. Use Up and Down arrow keys to navigate.'
               : 'No search suggestions found.'
         );
      };

      // Wrap clearResults
      var originalClear = window.PXUTheme.predictiveSearch.clearResults;
      window.PXUTheme.predictiveSearch.clearResults = function () {
         originalClear.apply(this, arguments);
         liveRegion.textContent = '';

         document.querySelectorAll('.search-form input[type="text"]').forEach(function (input) {
            resetIndex(input);
            input.setAttribute('aria-expanded', 'false');
            input.removeAttribute('aria-activedescendant');
         });
      };
   }

   // ============================================================================
   // 3. CART DRAWER ADA FIX
   // ============================================================================
   var _cartAdaPatched = false;
   function applyCartPatch() {
      if (_cartAdaPatched) return;
      _cartAdaPatched = true;
      console.log('ADA Fixes: Pure DOM Observer Attached for Cart!');

      var lastAjaxCartTrigger = null;
      var lastAnnouncedCartCount = null;

      // 1. Track the exact button that opened the cart
      document.addEventListener('click', function (e) {
         if (e.target.closest('.ajax-submit') || e.target.closest('[data-ajax-cart-trigger]')) {
             lastAjaxCartTrigger = document.activeElement;
         }
         
         // 2. Return focus perfectly when the cart is closed (clicking X or overlay)
         if (e.target.closest('[data-ajax-cart-close]') || e.target.closest('.ajax-cart__overlay') || e.target.closest('.ajax-cart__close')) {
             if (lastAjaxCartTrigger) {
                 setTimeout(function () {
                     if (lastAjaxCartTrigger && typeof lastAjaxCartTrigger.focus === 'function') {
                         lastAjaxCartTrigger.focus();
                         console.log("ADA Fixes: Focus returned to cart trigger.");
                     }
                     lastAjaxCartTrigger = null;
                 }, 300);
             }
         }
      }, true); // Use capture phase to ensure it runs before theme stops propagation

      // Helper function to wait for transition or fallback
      function focusAfterTransition(el, focusTarget) {
          var fired = false;
          var handler = function() {
              el.removeEventListener('transitionend', handler); // Clean up immediately, even on duplicate fires
              if (fired) return;
              fired = true;
              focusTarget.focus();
              console.log("ADA Fixes: Focus successfully shifted to:", focusTarget);
          };
          el.addEventListener('transitionend', handler);
          // Safety fallback if no transition occurs or takes too long
          setTimeout(handler, 400); 
      }

      // 3. Setup a Global Mutation Observer to detect when the Cart Drawer or Mini Cart visually opens
      var domObserver = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                  var target = mutation.target;
                  
                  // A. Detect Drawer Opening
                  if (target.matches('[data-ajax-cart-drawer]') || target.classList.contains('ajax-cart--drawer')) {
                      // If the drawer just received the 'is-visible' class
                      if (target.classList.contains('is-visible')) {
                          if (!target.hasAttribute('tabindex')) {
                              target.setAttribute('tabindex', '-1');
                              target.style.outline = 'none';
                          }
                          var focusTarget = target.querySelector('[data-ajax-cart-close], .ajax-cart__close, a[href], button:not([disabled])') || target;
                          focusAfterTransition(target, focusTarget);
                      }
                  }
                  
                  // B. Detect Mini Cart Opening
                  if (target.matches('[data-ajax-cart-trigger]') && target.classList.contains('show-mini-cart')) {
                      var dropdown = target.querySelector('.header-cart__mini-cart') || target;
                      if (!dropdown.hasAttribute('tabindex')) {
                          dropdown.setAttribute('tabindex', '-1');
                          dropdown.style.outline = 'none';
                      }
                      focusAfterTransition(dropdown, dropdown);
                  }
              }
          });
      });

      // Observe the entire body for class attribute changes (very lightweight)
      domObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class'] });

      // 4. Setup an observer specifically for the cart count updates
      var activeCountEl = null;
      var activeCountObserver = null;

      setInterval(function() {
          var countEl = document.querySelector('[data-bind="itemCount"]');
          
          if (countEl && countEl !== activeCountEl) {
              if (activeCountObserver) activeCountObserver.disconnect();
              activeCountEl = countEl;
              
              activeCountObserver = new MutationObserver(function() {
                  var count = activeCountEl.textContent.trim();
                  if (count !== '' && count !== lastAnnouncedCartCount) {
                      lastAnnouncedCartCount = count;
                      
                      var n = parseInt(count, 10);
                      var itemWord = n === 1 ? 'item' : 'items';
                      
                      // Trigger live region
                      announce('Cart updated. You now have ' + count + ' ' + itemWord + ' in your cart.');
                      console.log("ADA Fixes: Announced cart update:", count);

                      // Update all static aria-labels on cart triggers
                      document.querySelectorAll('[data-ajax-cart-trigger]').forEach(function (trigger) {
                          var el = trigger.querySelector('a') || trigger;
                          el.setAttribute('aria-label', 'Cart, ' + count + ' ' + itemWord);
                      });
                  }
              });
              
              activeCountObserver.observe(activeCountEl, { characterData: true, childList: true, subtree: true });
          }
      }, 1000);
   }

   // ============================================================================
   // 4. INITIALIZATION
   // ============================================================================
   function initializeADA() {
      appendLiveRegion();
      applySearchPatch();
      applyCartPatch();
   }

   document.addEventListener('DOMContentLoaded', initializeADA);
   window.addEventListener('load', initializeADA);

   var initAttempts = 0;
   var initInterval = setInterval(function () {
      initAttempts++;
      initializeADA();

      var searchPatched = window.PXUTheme && window.PXUTheme.predictiveSearch && window.PXUTheme.predictiveSearch._adaPatched;
      var cartPatched = _cartAdaPatched; 
      
      if ((searchPatched && cartPatched) || initAttempts > 40) {
         clearInterval(initInterval);
      }
   }, 250);

})();